import { getWeightedBoolean } from './random';
import { justRemove } from './array';
import {
  applyInlineError,
  inlineEntryHasMessage,
  InlineErrors
} from './inlineErrors';
import {
  fieldValues,
  filePathMap,
  fileDeduplicationCount,
  fileRetryStatus,
  initInfo,
  initState,
  setFieldValues
} from './init';
import throttle from 'lodash.throttle';
import { ACTION_EXECUTION_ORDER, ACTION_STORE_FIELD } from './elementActions';
import { featheryDoc, featheryWindow } from './browser';
import { getFilenameFromUrl } from './fileNames';
import { DEFAULT_MOBILE_BREAKPOINT } from '../elements/styles';
import internalState from './internalState';
import { setSavedStepKey } from './stepHelperFunctions';
import { createHttpHelpers } from '@feathery/client-utils';

export function isStoreFieldValueAction(el: any) {
  (el.properties?.actions ?? []).some(
    (action: any) => action.type === ACTION_STORE_FIELD
  );
}

export const getABVariant = (stepRes: any) => {
  stepRes.steps = stepRes.data;
  delete stepRes.data;
  if (!stepRes.variant) {
    delete stepRes.ab_test_data_weight;
    delete stepRes.ab_test_id;
    delete stepRes.ab_test_variant_a_weight;
    delete stepRes.ab_test_data_variant;
    return stepRes;
  }

  const { sdkKey, userId, overrideUserId } = initInfo();
  const dataWeight = stepRes.ab_test_data_weight;
  const sanitizedDataWeight =
    Number.isInteger(dataWeight) && dataWeight >= 1 && dataWeight <= 99
      ? dataWeight
      : 50;
  const variantAWeight = stepRes.ab_test_variant_a_weight;
  const dataVariant = stepRes.ab_test_data_variant;
  const canAssignByABTest =
    stepRes.ab_test_id &&
    Number.isInteger(variantAWeight) &&
    variantAWeight >= 1 &&
    variantAWeight <= 99 &&
    (dataVariant === 'variant_a' || dataVariant === 'variant_b');
  const shouldUseVariantPayload = () => {
    const assignedVariant = getWeightedBoolean(
      userId || sdkKey,
      stepRes.ab_test_id,
      variantAWeight
    )
      ? 'variant_a'
      : 'variant_b';
    return dataVariant !== assignedVariant;
  };
  // If userId was not passed in, sdkKey is assumed to be a user admin key
  // and thus a unique user ID.
  // If userId was preset (e.g. from _id URL param), skip AB variant
  // since the submission is tied to the original form.
  const useVariant =
    !overrideUserId &&
    (canAssignByABTest
      ? shouldUseVariantPayload()
      : !getWeightedBoolean(
          userId || sdkKey,
          stepRes.form_name,
          sanitizedDataWeight
        ));

  if (useVariant) {
    stepRes.new_form_id = stepRes.variant_id;
    stepRes.form_name = stepRes.variant_name;
    stepRes.version = stepRes.variant_version;
    stepRes.steps = stepRes.variant;
    stepRes.logic_rules = stepRes.variant_logic_rules;
    stepRes.shared_codes = stepRes.variant_shared_codes;
    stepRes.connector_fields = stepRes.variant_connector_fields;
  }

  delete stepRes.variant_id;
  delete stepRes.variant_name;
  delete stepRes.variant_version;
  delete stepRes.variant;
  delete stepRes.variant_logic_rules;
  delete stepRes.variant_shared_codes;
  delete stepRes.variant_connector_fields;
  delete stepRes.ab_test_data_weight;
  delete stepRes.ab_test_id;
  delete stepRes.ab_test_variant_a_weight;
  delete stepRes.ab_test_data_variant;
  return stepRes;
};

export const getAllElements = (step: any) => {
  return [
    ...step.progress_bars.map((e: any) => [e, 'progress_bar']),
    ...step.images.map((e: any) => [e, 'image']),
    ...step.videos.map((e: any) => [e, 'video']),
    // TODO (table): remove backwards incompatibility
    ...(step.tables?.map((e: any) => [e, 'table']) ?? []),
    ...step.texts.map((e: any) => [e, 'text']),
    ...step.buttons.map((e: any) => [e, 'button']),
    ...step.servar_fields.map((e: any) => [e, 'field']),
    ...step.subgrids.map((e: any) => [e, 'subgrid']),
    ...(step.tabs?.map((e: any) => [e, 'tab']) ?? [])
  ];
};

export const lookUpTrigger = (
  step: any,
  elementID: string,
  type: string,
  additionalPayload?: Record<string, any>
): Record<string, any> => {
  let payload = {};
  if (type === 'button') {
    const element = step.buttons.find((button: any) => button.id === elementID);
    payload = { text: element?.properties?.text ?? '' };
  } else if (type === 'text') {
    const element = step.texts.find((text: any) => text.id === elementID);
    payload = { text: element.properties.text };
  } else if (type === 'field') {
    const element = step.servar_fields.find(
      (field: any) => field.id === elementID
    );
    // servarId will remain undocumented and only used internally
    payload = {
      id: element.servar.key,
      _servarId: element.servar.id,
      text: element.servar.name
    };
  } else if (type === 'table') {
    // For tables, we don't look up additional properties from the step
    // All table-specific data is provided via additionalPayload
    payload = {};
  }
  return { id: elementID, type, ...payload, ...additionalPayload };
};

/** Update the fieldValues cache with a backend session */
export function updateSessionValues(session: any) {
  processFileValues(session.file_values);

  const replaceNullInServarArrays = (
    acc: Record<string, any>,
    [key, value]: [string, any]
  ) => {
    if (Array.isArray(value) && session.servars.includes(key)) {
      acc[key] = value.map((item) => (item === null ? '' : item));
    } else {
      acc[key] = value;
    }
    return acc;
  };

  const transformedFieldValues: Record<string, any> = Object.entries(
    session.field_values
  ).reduce(replaceNullInServarArrays, {});

  // processFileValues owns these keys. The backend routes file servars into
  // file_values alone, so this only bites if that split changes -- but '' here
  // would flatten a repeat hole and silently undo it.
  Object.keys(session.file_values ?? {}).forEach(
    (key) => delete transformedFieldValues[key]
  );

  Object.assign(fieldValues, transformedFieldValues);
}

/**
 * Set an error on a particular form DOM node(s).
 */
// The sole writer of the InlineErrors invariant (row-scoped `byIndex` entries
// under real field keys), so keep at least the error-map-shaping inputs typed.
interface SetFormElementErrorArgs {
  formRef?: React.MutableRefObject<any>;
  errorType?: string;
  errorCallback?: (props: Record<string, unknown>) => unknown;
  fieldKey?: string;
  message?: string;
  index?: number | null;
  servarType?: string;
  inlineErrors?: InlineErrors;
  setInlineErrors?: (errors: InlineErrors) => void;
  triggerErrors?: boolean;
}

export async function setFormElementError({
  formRef,
  errorType,
  errorCallback = () => {},
  fieldKey = '',

  // Empty message means no error / clearing the error
  message = '',

  index = null,
  servarType = '',
  inlineErrors = {},
  setInlineErrors = () => {},
  triggerErrors = false
}: SetFormElementErrorArgs) {
  let invalid = false;
  let listIndex = index;
  if (errorType === 'html5') {
    const form = formRef?.current;
    if (!form) return false;

    let errorTriggered = false;
    if (fieldKey) {
      if (['pin_input', 'select', 'multiselect'].includes(servarType))
        fieldKey = `${fieldKey}-0`;
      // Add repeat index to fieldKey for fields using radio inputs
      if (index != null && ['matrix', 'select'].includes(servarType)) {
        fieldKey = `${fieldKey}-${index}`;
        // no longer use index to key into element list
        listIndex = null;
      }
      // form.elements has reserved props so must use namedItem to get by id
      const singleOrList = form.elements.namedItem(fieldKey);
      let elements =
        singleOrList instanceof RadioNodeList
          ? Array.from(singleOrList)
          : [singleOrList];
      // Hidden inputs are value mirrors (see HiddenValueInput) and are barred
      // from constraint validation, so they must never be targeted for, or
      // shift the indexing of, error display
      elements = elements.filter((e) => e && (e as any).type !== 'hidden');

      if (listIndex !== null && elements.length)
        elements = [elements[listIndex]];

      elements.forEach((element) => {
        if (!element) return;

        // If we are targeting a non-submit button, we instead target its hidden input child
        if (element.tagName === 'BUTTON' && element.type !== 'submit') {
          element = element.querySelector(`#error_${element.id}`);
          // Only ButtonElement renders that child; any other button that
          // resolves under a field key has nothing to carry the error
          if (!element) return;
        }
        element.setCustomValidity(message);
        if (triggerErrors) {
          // Trigger manually-set errors first before other form errors
          element.reportValidity();
          errorTriggered = true;
        }
      });
    }
    if (triggerErrors && !errorTriggered) {
      // Find the first visible invalid element to show the browser tooltip.
      // Calling reportValidity() on the entire form fails if the first
      // invalid control is hidden via CSS (display:none).
      const formElements = Array.from(form.elements) as HTMLElement[];
      for (const el of formElements) {
        if (
          'checkValidity' in el &&
          !(el as HTMLInputElement).checkValidity() &&
          el.offsetParent !== null
        ) {
          (el as HTMLInputElement).reportValidity();
          break;
        }
      }
    }
    invalid = !form.checkValidity();
  } else if (errorType === 'inline') {
    // Scope a repeated-field error to its row via a nested `byIndex` map under
    // the real field key, so an error validated on one repeat row doesn't bleed
    // onto other rows (including freshly added, untouched ones) and can never
    // collide with a literal field key such as `foo-0`. An empty message clears
    // (whole field for a non-indexed write, only that row for an indexed one).
    applyInlineError(inlineErrors, fieldKey, message, index);
    if (triggerErrors)
      setInlineErrors(JSON.parse(JSON.stringify(inlineErrors)));
    invalid = Object.values(inlineErrors).some((data) =>
      inlineEntryHasMessage(data as any)
    );
  }
  if (message) {
    await errorCallback({
      errorFieldId: fieldKey,
      errorFieldType: servarType,
      errorMessage: message,
      elementRepeatIndex: index || 0
    });
  }
  return invalid;
}

const clearBrowserErrorsDebounced = throttle(
  (formRef: React.MutableRefObject<any>) => {
    if (!formRef.current) return;
    Array.from(formRef.current.elements).forEach((element: any) => {
      element.setCustomValidity('');
    });
  },
  1000
);

export function clearBrowserErrors(formRef: React.MutableRefObject<any>) {
  clearBrowserErrorsDebounced(formRef);
}

export function objectMap(obj: any, transform: any) {
  return Object.entries(obj).reduce((newObj, [key, val]) => {
    return { ...newObj, [key]: transform(val) };
  }, {});
}

/**
 * If a user's file is already uploaded, Feathery backend returns S3 details: { path, url }
 * We convert this information into Promises that resolve to the file
 */
export async function fetchS3File(url: any) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new File([blob], getFilenameFromUrl(url), {
    type: blob.type
  });
}

/**
 * Process file_values from a backend response: fetch files from S3 and
 * update fieldValues + filePathMap. Used by session, logic rule, and
 * extraction completion responses.
 */
export function processFileValues(fileValues: Record<string, any>) {
  if (!fileValues || Object.keys(fileValues).length === 0) return;

  // A repeated file field sends null for a row that holds no file. Those holes
  // have to survive rehydration or the indices collapse again on reload.
  const filePromises = objectMap(fileValues, (fileOrFiles: any) =>
    Array.isArray(fileOrFiles)
      ? fileOrFiles.map((f: any) => (f?.url ? fetchS3File(f.url) : null))
      : fetchS3File(fileOrFiles.url)
  );

  const newFilePathMap = objectMap(fileValues, (fileOrFiles: any) =>
    Array.isArray(fileOrFiles)
      ? fileOrFiles.map((f: any) => f?.path ?? null)
      : fileOrFiles.path
  );

  Object.assign(fieldValues, filePromises);
  Object.assign(filePathMap, newFilePathMap);
}

// Drop one repeat row's entry so the remaining paths stay aligned with their
// rows. Deduplication is reset so the corrected file list is actually resent.
export function removeFilePathMapEntry(key: any, index: number) {
  const paths = filePathMap[key];
  if (Array.isArray(paths)) filePathMap[key] = justRemove(paths, index);
  delete fileDeduplicationCount[key];
}

// Update the map we maintain to track files that have already been uploaded to S3
// This means nulling the existing mapping because the user uploaded a new file
export function clearFilePathMapEntry(key: any, index = null) {
  if (index !== null) {
    if (!filePathMap[key]) filePathMap[key] = [];
    // @ts-ignore
    filePathMap[key][index] = null;
  } else {
    filePathMap[key] = null;
  }
  delete fileDeduplicationCount[key];
  if (key in fileRetryStatus) {
    fileRetryStatus[key] = false;
  }
}

export function registerRenderCallback(
  internalId: string,
  key: 'form' | 'loginForm',
  callback: () => void
) {
  initState.renderCallbacks[internalId] = {
    ...initState.renderCallbacks[internalId],
    [key]: callback
  };
}

export function rerenderAllForms() {
  Object.values(initInfo().renderCallbacks).forEach(
    (formCbs: Record<string, any>) =>
      Object.values(formCbs).forEach((cb: any) => cb())
  );
}

export function remountAllForms(saveCurrentStep?: boolean) {
  if (saveCurrentStep) {
    // Save current step keys from internal state before remounting
    Object.entries(internalState).forEach(([formId, state]) => {
      if (state?.currentStep?.key) {
        setSavedStepKey(formId, state.currentStep.key);
      }
    });
  }
  Object.values(initInfo().remountCallbacks).forEach((cb) => cb());
}

// Reorders by leaving non-execution order actions in place and moving actons with specific
// exceution orders to the end.  Non-priority order actions are
// essenially order 0 (before others).
export function prioritizeActions(actions: any[]) {
  const newActions = [...actions];
  Object.entries(ACTION_EXECUTION_ORDER)
    .sort((a, b) => a[1] - b[1])
    .forEach(([action]) => {
      const index = newActions.findIndex((a) => a.type === action);
      if (index > -1) {
        newActions.push(newActions.splice(index, 1)[0]);
      }
    });
  return newActions;
}

export function mapFormSettingsResponse(res: any) {
  return {
    authSensitiveActionsOnly: res.auth_sensitive_actions_only ?? false,
    errorType: res.error_type,
    autocomplete: res.autocomplete ? 'on' : 'off',
    autofocus: res.autofocus,
    allowEdits: res.allow_edits,
    completionBehavior: res.completion_behavior,
    showBrand: Boolean(res.show_brand),
    brandPosition: res.brand_position,
    autoscroll: res.autoscroll,
    rightToLeft: res.right_to_left,
    saveUrlParams: res.save_url_params,
    enterToSubmit: res.enter_submit,
    globalStyles: res.global_styles,
    saveHideIfFields: res.save_hide_if_fields,
    clearHideIfFields: res.clear_hide_if_fields,
    // Both default on, so a cached payload from before the settings existed
    // keeps reporting progress
    showFileUploadProgress: res.show_file_upload_progress ?? true,
    showDocumentProgress: res.show_document_progress ?? true,
    mobileBreakpoint: res.mobile_breakpoint ?? DEFAULT_MOBILE_BREAKPOINT,
    assistantEnabled: res.ai_assistant_settings?.enabled,
    assistantColor: res.ai_assistant_settings?.color
      ? `#${res.ai_assistant_settings.color}`
      : '#6b7280',
    assistantWorkflowActions: res.ai_assistant_settings?.workflow_actions ?? [],
    assistantStepSettings: res.ai_assistant_settings?.step_settings ?? {}
  };
}

export async function updateCustomHead(headCode: string) {
  if (!headCode) return;

  const parser = new DOMParser();
  const doc = parser.parseFromString(headCode, 'text/html');

  const scriptWait: any[] = [];
  doc.querySelectorAll('*').forEach((custom) => {
    if (['HEAD', 'HTML', 'BODY'].includes(custom.tagName)) return;
    if (custom.tagName === 'SCRIPT') {
      const customScript = custom as HTMLScriptElement;
      // Parsed script cannot be added directly, must be transferred to a
      // created element
      const el = featheryDoc().createElement(customScript.tagName);
      Array.from(customScript.attributes).forEach((attr) => {
        el.setAttribute(attr.name, attr.value);
      });
      // Copy inline script content
      el.textContent = customScript.textContent;
      if (el.src) {
        scriptWait.push(
          new Promise((resolve) => {
            el.onload = () => resolve(custom);
            el.onerror = () => resolve(custom);
          })
        );
      }
      custom = el;
    }
    featheryDoc().head.appendChild(custom);
  });
  await Promise.all(scriptWait);
}

export function updateCustomCSS(cssCode: string) {
  if (!cssCode) return;

  const style = featheryDoc().createElement('style');
  style.textContent = cssCode;
  featheryDoc().head.appendChild(style);
}

function getConnectorFieldValues(connectorFields: string[]) {
  const _fieldValues: { [key: string]: any } = connectorFields
    .filter((fieldKey) => !(fieldKey in filePathMap))
    .reduce(
      (acc, fieldKey) => ({
        ...acc,
        [fieldKey]: fieldValues[fieldKey]
      }),
      {}
    );
  _fieldValues.feathery_user_id = initState.userId;
  return _fieldValues;
}

export function isElementInViewport(el: any) {
  const rect = el.getBoundingClientRect();

  const height =
    featheryWindow().innerHeight || featheryDoc().documentElement.clientHeight;
  const width =
    featheryWindow().innerWidth || featheryDoc().documentElement.clientWidth;
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= height &&
    rect.right <= width
  );
}

export function httpHelpers(client: any, connectorFields: string[] = []) {
  const { sdkKey, userId } = initInfo();
  return createHttpHelpers(
    sdkKey,
    client.formKey,
    userId,
    () => getConnectorFieldValues(connectorFields),
    (fieldValues) => {
      // skip server submit when setting field values here
      // because these values were just created on the server
      setFieldValues(fieldValues, true, true);
    },
    client.offlineRequestHandler.runOrSaveRequest.bind(
      client.offlineRequestHandler
    )
  );
}
